import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { RefreshCw, Loader2, Inbox, CheckCircle2, XCircle } from "lucide-react";

/**
 * Email Outbox Viewer — reads every row from `email_outbox` for the current org so admins
 * can diagnose bounces, retry misfires, and audit compliance emails.
 */
export default function EmailOutboxPanel() {
  const [data, setData] = useState({ rows: [], stats: {} });
  const [kind, setKind] = useState("all");
  const [status, setStatus] = useState("all");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const params = {};
      if (kind !== "all") params.kind = kind;
      if (status !== "all") params.status = status;
      const r = await api.get("/email/outbox", { params });
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind, status]);

  const st = data.stats || {};
  const kindLabel = { report: "Report", digest: "Digest", sla_breach: "SLA breach", test: "Test" };
  const kindColor = { report: "bg-blue-50 text-blue-700 border-blue-200",
                       digest: "bg-purple-50 text-purple-700 border-purple-200",
                       sla_breach: "bg-red-50 text-red-700 border-red-200",
                       test: "bg-slate-50 text-slate-700 border-slate-200" };

  return (
    <div className="card-sharp p-5 space-y-4" data-testid="email-outbox-panel">
      <div className="flex items-center justify-between border-b pb-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent flex items-center gap-1.5">
            <Inbox className="w-3.5 h-3.5" /> Email Outbox · Delivery log
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            Every mail sent by Zenith 360 — reports, compliance digests, SLA-breach alerts and tests.
            Diagnose bounces here by matching the <code>provider_id</code> in your Resend dashboard.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="rounded-sm gap-1 border-emerald-200 text-emerald-700 bg-emerald-50">
            <CheckCircle2 className="w-3 h-3" /> {st.sent || 0} sent
          </Badge>
          {(st.failed || 0) > 0 && (
            <Badge variant="outline" className="rounded-sm gap-1 border-red-200 text-red-700 bg-red-50">
              <XCircle className="w-3 h-3" /> {st.failed} failed
            </Badge>
          )}
        </div>
      </div>

      <div className="flex items-end gap-2">
        <div className="w-40">
          <div className="text-[10px] font-mono uppercase text-muted-foreground mb-1">Kind</div>
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger className="rounded-sm h-9 text-xs" data-testid="outbox-filter-kind">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All ({st.total || 0})</SelectItem>
              <SelectItem value="report">Reports ({st.reports || 0})</SelectItem>
              <SelectItem value="digest">Digests ({st.digests || 0})</SelectItem>
              <SelectItem value="sla_breach">SLA breach ({st.sla_breach || 0})</SelectItem>
              <SelectItem value="test">Test ({st.test || 0})</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-32">
          <div className="text-[10px] font-mono uppercase text-muted-foreground mb-1">Status</div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="rounded-sm h-9 text-xs" data-testid="outbox-filter-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" onClick={load} disabled={busy} className="rounded-sm gap-1.5 ml-auto"
                 data-testid="outbox-refresh">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-4 h-4" /> Refresh</>}
        </Button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full dense">
          <thead>
            <tr>
              <th>Sent at</th>
              <th>Kind</th>
              <th>To</th>
              <th>From</th>
              <th>Subject</th>
              <th>Status</th>
              <th>Provider ID</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.length === 0 && (
              <tr><td colSpan={7} className="text-center text-muted-foreground py-10">
                No emails in the outbox yet — sending anything from a "Send by email" button will show up here.
              </td></tr>
            )}
            {data.rows.map((e, i) => (
              <tr key={i} data-testid={`outbox-row-${i}`}>
                <td className="text-xs font-mono whitespace-nowrap">
                  {e.created_at ? new Date(e.created_at).toLocaleString("en-IN") : "—"}
                </td>
                <td>
                  <span className={`text-[9px] font-mono px-1.5 py-0.5 border rounded-sm uppercase ${kindColor[e.kind] || "bg-slate-50 border-slate-200"}`}>
                    {kindLabel[e.kind] || e.kind || "—"}
                  </span>
                </td>
                <td className="text-xs font-mono">{e.to}</td>
                <td className="text-xs font-mono max-w-[220px] truncate" title={e.from}>{e.from}</td>
                <td className="text-xs max-w-[260px] truncate" title={e.subject}>{e.subject}</td>
                <td>
                  {e.status === "sent" ? (
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-200">SENT</span>
                  ) : e.status === "failed" ? (
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-red-50 text-red-700 border border-red-200" title={e.error}>FAILED</span>
                  ) : (
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-amber-50 text-amber-700 border border-amber-200">{e.status || "unknown"}</span>
                  )}
                </td>
                <td className="text-[10px] font-mono text-muted-foreground max-w-[180px] truncate" title={e.provider_id}>
                  {e.provider_id ? e.provider_id.slice(0, 16) + "…" : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
